import { ReactNode, useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useMutation, useServerErrors } from '../../Shared/api';
import { useNotifier } from '../../Shared/Notifier/NotificationProvider';
import { Button } from '../../Shared/UI/Button';
import { Card } from '../../Shared/UI/Card';
import ErrorMessage from '../../Shared/UI/ErrorMessage';
import { Icon } from '../../Shared/UI/Icon';
import InfoMessage from '../../Shared/UI/InfoMessage';
import { Input } from '../../Shared/UI/Input';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import ServerErrors from '../../Shared/UI/ServerErrors';
import { useRouter } from '../router';
import { Charity } from '../types';
import Layout from '../UI/Layout';

export interface EditIntegrationsFormProps {
  charity: Charity;
}

export default function EditIntegrationsForm({
  charity,
}: EditIntegrationsFormProps) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const form = useForm({
    defaultValues: {
      google_analytics_id: charity.google_analytics_id,
    },
  });

  const {
    register, handleSubmit,
    formState: { defaultValues, errors },
  } = form;

  const [saveIntegrations, { errors: serverErrors, loading }] = useMutation(
    routes.save_integrations(),
    {
      preserveState: 'errors',
      onSuccess: () => {
        notifier.notify(t('backend:information_updated'));
      },
    }
  );

  useServerErrors(form, serverErrors);

  const submit = async (data: typeof defaultValues) => {
    await saveIntegrations(data);
  };

  const [showGoogleAnalyticsInstructions, setShowGoogleAnalyticsInstructions] = useState(false);

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Card panels header footer>
        <div>
          <h3>
            {t('backend:integrations')}
          </h3>
        </div>
        <div className="space-y-6">
          {serverErrors && <ServerErrors errors={serverErrors} scrollIntoView />}

          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:google_analytics')}
            </h4>
            <div className="text-slate-500">
              {t('backend:google_analytics_description')}
            </div>
          </div>

          <InputGroup>
            <InputLabel htmlFor="integrations_google_analytics_id">
              {t('backend:google_analytics_measurement_id')}
            </InputLabel>
            <Input
              {...register('google_analytics_id')}
              id="integrations_google_analytics_id"
              aria-invalid={!!errors.google_analytics_id}
              placeholder="G—××××××××××"
            />
            <ErrorMessage error={errors.google_analytics_id} attribute={t('backend:google_analytics_measurement_id')} />
          </InputGroup>

          <InfoMessage>
            <a
              onClick={() => setShowGoogleAnalyticsInstructions(!showGoogleAnalyticsInstructions)}
              role="button"
              className="text-blue-600 font-medium hover:text-blue-500"
            >
              {t('backend:show_instructions')}
              <Icon className="ml-1">
                {showGoogleAnalyticsInstructions ? <ChevronUp /> : <ChevronDown />}
              </Icon>
            </a>
            {showGoogleAnalyticsInstructions && (
              <>
                <h4 className="mt-3">
                  {t('backend:google_analytics_instructions_website_title')}
                </h4>
                <ol className="mb-2 list-decimal pl-6">
                  <li>
                    {t('backend:google_analytics_instructions_website_description_1')}
                  </li>
                  <li>
                    {t('backend:google_analytics_instructions_website_description_2')}
                  </li>
                  <li>
                    {t('backend:google_analytics_instructions_website_description_3')}
                  </li>
                  <li>
                    {t('backend:google_analytics_instructions_website_description_4')}
                  </li>
                </ol>

                <h4>
                  {t('backend:google_analytics_instructions_supporta_title')}
                </h4>
                <ol className="mb-2 list-decimal pl-6">
                  <li>
                    {t('backend:google_analytics_instructions_supporta_description_1')}
                  </li>
                  <li>
                    {t('backend:google_analytics_instructions_supporta_description_2')}
                  </li>
                </ol>

                <h4>
                  {t('backend:google_analytics_instructions_events_title')}
                </h4>

                <ul>
                  <li>
                    <span className="bg-blue-200 font-medium rounded-sm text-blue-700 mr-1 text-sm px-1.5 inline-block">
                        supporta_new_campaign
                    </span>
                    {' '}
                    {t('backend:google_analytics_instructions_new_campaign_event')}
                  </li>
                  <li>
                    <span className="bg-blue-200 font-medium rounded-sm text-blue-700 mr-1 text-sm px-1.5 inline-block">
                        purchase
                    </span>
                    {' '}
                    {t('backend:google_analytics_new_donation_event')}
                  </li>
                </ul>
              </>
            )}
          </InfoMessage>
        </div>
        <div className="text-right">
          <Button
            type="submit"
            loading={loading}
            className="ml-auto"
          >
            {t('backend:save')}
          </Button>
        </div>
      </Card>
    </form>
  );
}

EditIntegrationsForm.layout = (page: ReactNode) => <Layout>{page}</Layout>;
